/**
 * analytics.js — thin wrapper around Google Analytics (GA4).
 *
 * The gtag.js snippet is injected in /app/frontend/public/index.html with
 * `send_page_view: false`, so this module owns every pageview and custom
 * event. React Router controls navigation, and RouteAnalytics (in App.js)
 * calls `trackPageview` on every location change — including the first
 * paint — so GA sees exactly one page_view per SPA navigation.
 *
 * All helpers are safe no-ops when gtag hasn't loaded (ad blockers,
 * preview environments without network access, offline).
 */

// Sourced from REACT_APP_GA4_MEASUREMENT_ID (frontend/.env). Fallback matches
// the ID hardcoded in public/index.html's <script> tag so `send_to` on events
// stays in sync even if the env var goes missing (CRA can't template static HTML).
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-0CN4PMVD9E";

const gtagReady = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

/**
 * Fire a GA4 page_view. Pass the current path + querystring; GA4 also
 * captures the full URL and referrer automatically from `page_location`
 * and `page_referrer` params, which we set explicitly for SPA fidelity.
 */
export function trackPageview(pathWithSearch) {
  if (!gtagReady()) return;
  const url = typeof window !== "undefined" ? window.location.href : pathWithSearch;
  window.gtag("event", "page_view", {
    page_path: pathWithSearch,
    page_location: url,
    page_title: typeof document !== "undefined" ? document.title : undefined,
    send_to: GA_MEASUREMENT_ID,
  });
}

/**
 * Generic custom event fire. `params` is a flat object; GA4 accepts up
 * to 25 custom parameters per event. Values must be primitives.
 */
export function trackEvent(name, params = {}) {
  if (!gtagReady()) return;
  window.gtag("event", name, { send_to: GA_MEASUREMENT_ID, ...params });
}

/**
 * Beta waitlist / donate-coming-soon / student-coming-soon signup.
 * Fires `beta_signup` with the role and a coarse source label so we
 * can slice conversion in GA4's Realtime + Explore reports.
 *
 * Note: kept role/source lowercase and free of PII (no email address).
 */
export function trackBetaSignup({ role, source, alreadySignedUp = false } = {}) {
  trackEvent("beta_signup", {
    role: role || "unknown",
    source: source || "unknown",
    already_signed_up: !!alreadySignedUp,
  });
}

/**
 * Give-button clicked, right before we hand the donor off to Stripe.
 * Fires `donate_click` so GA4 can measure top-of-funnel conversion
 * even for donors who bounce at the Stripe hosted checkout.
 *
 *   campaignId — undefined for General Fund donations
 *   amountUsd  — numeric dollars (2500 cents → 25.00). May be omitted
 *                if the donor never selected a preset (custom flow).
 *   isRecurring— true when the donor toggled monthly.
 */
export function trackDonateClick({ campaignId, amountUsd, isRecurring = false } = {}) {
  trackEvent("donate_click", {
    campaign_id: campaignId || "general",
    amount_usd: amountUsd || 0,
    is_recurring: !!isRecurring,
  });
}

/**
 * Extract GA4's client_id from the _ga cookie.
 * Cookie format: `GA1.2.<clientId1>.<clientId2>` → returns "<clientId1>.<clientId2>"
 * so the backend's Measurement Protocol call can attribute the
 * server-side donation_completed event to the same visitor.
 *
 * Returns null when the cookie isn't set (e.g., ad-blocked browsers) —
 * caller must handle gracefully.
 */
export function getGa4ClientId() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )_ga=([^;]+)/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  const parts = raw.split(".");
  if (parts.length < 4) return null;
  return `${parts[2]}.${parts[3]}`;
}
